import { Component } from '@angular/core';
import { Paginator } from '@app/classes/paginator';
import { ConfirmationDialogComponent } from '@app/components';
import { PendingPayrollStatus } from '@app/interfaces/pending-payroll-status.interface';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { IncomeTypeRequest } from '@app/models/payroll/income-type-request.model';
import { BaseForm } from '@app/modules/forms/base.form';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { NotifyService, PayrollService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { TranslatableKey } from '@app/types/translatable.type';
import { IndexView } from '@common/views/index/index.view';

type CompanyAdditionalIncomeDialogType =
    | 'REQUEST_CUSTOM_INCOME_TYPE'
    | 'EDIT_COMPANY_ADDITIONAL_INCOME'
    | 'DELETE_ADDITIONAL_INCOME';

@Component({
    templateUrl: './additional-income-index.template.html',
    styleUrls: ['./additional-income-index.style.scss'],
})
export class CompanyAdditionalIncomeIndexView extends IndexView {
    isLoading = true;
    paginator!: Paginator<CompanyAdditionalIncome>;
    inactiveCompanyAdditionalIncomes: CompanyAdditionalIncome[] = [];
    pendingPayrollExists = false;
    currentlyOpenDialog?: CompanyAdditionalIncomeDialogType = undefined;

    get showAddIncomeType(): boolean {
        return Boolean(this.inactiveCompanyAdditionalIncomes && this.inactiveCompanyAdditionalIncomes.length);
    }

    get activeCompanyAdditionalIncomes(): CompanyAdditionalIncome[] {
        return this.paginator.values || [];
    }

    constructor(
        private auth: AuthService,
        private notify: NotifyService,
        private payrollService: PayrollService,
        public payrollModuleMenuService: PayrollModuleMenuService
    ) {
        super();
        this.loadData();
    }

    getRemoveTooltipText(companyAdditionalIncome: CompanyAdditionalIncome): TranslatableKey {
        if (companyAdditionalIncome.required) {
            return 'payroll.additional-incomes.cannotRemoveIncomeType';
        }

        return 'payroll.additional-incomes.removeIncomeType';
    }

    getAllCompanyAdditionalIncomes(): CompanyAdditionalIncome[] {
        return [...this.activeCompanyAdditionalIncomes, ...this.inactiveCompanyAdditionalIncomes];
    }

    async onSave(companyAdditionalIncome: CompanyAdditionalIncome | null = null): Promise<void> {
        if (companyAdditionalIncome) {
            companyAdditionalIncome.active = true;
            await companyAdditionalIncome.param('company', this.auth.company?.id).save();
            this.loadData();
            this.onCancel();
        }
    }

    async onRequestIncomeType(incomeTypeRequest: IncomeTypeRequest, form: BaseForm): Promise<void> {
        try {
            await incomeTypeRequest.param('company', this.auth.company?.id).save();
            this.notify.success('payroll.additional-incomes.incomeTypeRequested');
        } catch (err) {
            this.notify.error('payroll.additional-incomes.errorHumiNotified');
            throw err;
        }

        form.reset();
        this.onCancel();
    }

    /**
     * Check if the income type can be disabled
     */
    canRemove(companyAdditionalIncome: CompanyAdditionalIncome): boolean {
        return (
            !companyAdditionalIncome.required &&
            !this.pendingPayrollExists &&
            !(
                companyAdditionalIncome.timeOffEmployeeHoursExist ||
                companyAdditionalIncome.timeTrackingEmployeeHoursExists
            )
        );
    }

    async onRemove(form: ConfirmationDialogComponent, companyAdditionalIncome: CompanyAdditionalIncome): Promise<void> {
        if (!(await form.open())) {
            return;
        }

        this.isLoading = true;
        companyAdditionalIncome.active = false;
        await companyAdditionalIncome.param('company', this.auth.company?.id).save();
        this.loadData();
    }

    onCancel(): void {
        this.currentlyOpenDialog = undefined;
    }

    openRequestIncomeType(): void {
        this.currentlyOpenDialog = 'REQUEST_CUSTOM_INCOME_TYPE';
    }

    onCreate(): void {}

    private async loadData(): Promise<void> {
        this.isLoading = true;
        this.checkPendingPayrollStatus();
        this.paginator = new Paginator(
            CompanyAdditionalIncome.param('company', this.auth.company?.id).where('active', true),
            { shouldAutoLoadData: true }
        );
        // The loading indicator will turn off when this event is received
        this.paginator.onLoading((isLoading) => (this.isLoading = isLoading));
        const [inactiveCompanyAdditionalIncomes] = await CompanyAdditionalIncome.param('company', this.auth.company?.id)
            .where('active', false)
            .get();
        this.inactiveCompanyAdditionalIncomes = inactiveCompanyAdditionalIncomes;
    }

    private checkPendingPayrollStatus(): void {
        this.payrollService
            .getPendingPayrollStatus()
            .then(({ exists }: PendingPayrollStatus) => (this.pendingPayrollExists = exists));
    }
}
